import { JSONObject } from 'module/types/common';
import { transformJSONFromOld } from 'api/custom-section/transformer';
import {
  ActionCustomSectionConfig,
  CustomSectionNode,
  CustomSectionType,
  QuestionCustomSectionConfig,
  ExtractInfoCustomSectionConfig,
  MeetingDetailsCustomSectionConfig,
  AccountDetailsCustomSectionConfig,
  TextBlockCustomSectionConfig,
} from 'api/custom-section/types';
import { CompanyPageSourceDerivedSectionConfig } from 'api/custom-section/types/company-page-derived-section';
import { SignalCustomSectionConfig } from 'api/custom-section/types/signal';
import { HttpClient } from 'api/http-client';
import { PageType } from 'api/page-template/types';
import { EmailPage, Page } from './types';

export type MeetingListType = {
  meetingId: number;
  meetingDocumentMappingId: number;
  title: string;
  documentId: number;
  updatedAt: string;
  createdAt: string;
  startTime: string;
};

export enum StatusDocument {
  PROGRESS = 'PROGRESS',
  COMPLETED = 'COMPLETED',
}

export type Document = {
  content: JSONObject;
  metadata: {
    accountId: number;
    documentId: number;
    editorDocumentName: string;
    title: string;
    updatedAt: string;
    createdAt: string;
    status?: StatusDocument;
    documentSourceMappingModel?: {
      pageType: PageType;
      sourceId: number;
      pageDocumentMappingId: number;
      metadata: {
        documentAgentId: number;
        processed: boolean;
        updating: boolean;
        sources?: Array<{
          sourceId: number;
          sourceType: string;
          timestamp?: string;
        }>;
      };
    };
  };
};

export type SaveDocumentPayload = {
  tiptapJson: JSONObject;
  htmlContent: string;
};
type BaseMeetingTemplateSection = {
  title: string;
  identifier: string;
};
type QuestionMeetingTemplateSection = BaseMeetingTemplateSection & {
  sectionType: CustomSectionType.Question;
  config: QuestionCustomSectionConfig;
};
type ActionMeetingTemplateSection = BaseMeetingTemplateSection & {
  sectionType: CustomSectionType.Action;
  config: ActionCustomSectionConfig;
};
type ExtractInfoMeetingTemplateSection = BaseMeetingTemplateSection & {
  sectionType: CustomSectionType.ExtractInfo;
  config: ExtractInfoCustomSectionConfig;
};
type SignalMeetingTemplateSection = BaseMeetingTemplateSection & {
  sectionType: CustomSectionType.SignalDetails;
  config: SignalCustomSectionConfig;
};

type MeetingDetailsMeetingTemplateSection = BaseMeetingTemplateSection & {
  sectionType: CustomSectionType.MeetingDetails;
  config: MeetingDetailsCustomSectionConfig;
};

type AccountDetailsMeetingTemplateSection = BaseMeetingTemplateSection & {
  sectionType: CustomSectionType.AccountDetails;
  config: AccountDetailsCustomSectionConfig;
};

type TextBlockMeetingTemplateSection = BaseMeetingTemplateSection & {
  sectionType: CustomSectionType.CustomTextSection;
  config: TextBlockCustomSectionConfig;
};

type CompanyPageSourceDerivedSection = BaseMeetingTemplateSection & {
  sectionType: CustomSectionType.CompanyPageSourceDerivedSection;
  config: CompanyPageSourceDerivedSectionConfig;
};

export type MeetingTemplateSection =
  | QuestionMeetingTemplateSection
  | ActionMeetingTemplateSection
  | ExtractInfoMeetingTemplateSection
  | SignalMeetingTemplateSection
  | MeetingDetailsMeetingTemplateSection
  | AccountDetailsMeetingTemplateSection
  | TextBlockMeetingTemplateSection
  | CompanyPageSourceDerivedSection;

type ResolveCustomSectionResponse = {
  finalContent?: JSONObject;
  originalContent?: JSONObject;
  /** Group section: streaming credentials and list of section IDs to stream */
  streamingMeta?: { channelName: string; token: string };
  templateSectionIdList?: number[];
};

class PageClient extends HttpClient {
  public getPages<PageTypeT extends PageType, PageT extends Page>(
    type: PageTypeT,
    companyId?: number,
  ): Promise<PageT[]> {
    return this.get('/pages', {
      params: {
        pageType: type,
        companyId,
      },
    });
  }
  public getDefaultTemplateSections(
    pageType: PageType,
  ): Promise<MeetingTemplateSection[]> {
    return this.get(`/pages/template/sections?pageType=${pageType}`);
  }
  public resolveCustomSection(
    documentId: number,
    node: CustomSectionNode,
  ): Promise<ResolveCustomSectionResponse> {
    return this.post(`/pages/${documentId}/section?streaming=true`, {
      originalContent: node,
    });
  }
  public getDocument(id: number): Promise<Document> {
    return this.get<Document>(`/scarlet/documents/${id}`).then((response) => ({
      content: transformJSONFromOld(response.content),
      metadata: response.metadata,
    }));
  }

  public getDocumentVersionHistory(documentId: number): Promise<unknown[]> {
    return this.get<unknown[]>(
      `/scarlet/documents/${documentId}/version-history`,
    );
  }

  public saveDocument(id: number, payload: SaveDocumentPayload): Promise<void> {
    return this.put(`/scarlet/documents/${id}`, {
      tiptapJson: payload.tiptapJson,
      htmlContent: payload.htmlContent,
    });
  }

  public updateDocumentTitle(id: number, title: string): Promise<void> {
    return this.put(`/scarlet/documents/${id}`, { title });
  }
  public getGroupedPages(
    pageType: PageType,
    companyId: number,
  ): Promise<EmailPage[]> {
    return this.get(
      `/pages/get-pages-grouped?pageType=${pageType}&companyId=${companyId}`,
    );
  }
  public getCollaborationToken(): Promise<{ collabToken: string }> {
    return this.get('/scarlet/documents/editor/getCollabToken');
  }

  public createDocument(payload: {
    title: string;
    content: JSONObject;
    addMapping: boolean;
    mappingModel?: {
      pageType: string;
      sourceId?: number;
    };
  }): Promise<{ documentId: number; [key: string]: any }> {
    return this.post('/scarlet/documents', payload);
  }

  public getPagesByTypeList(
    companyId: number,
    pageTypeList: string,
  ): Promise<Page[]> {
    return this.get('/pages/multiple', {
      params: {
        pageTypeList,
        companyId,
      },
    });
  }

  public deletePage(
    pageDocumentMappingId: number,
    pageType: PageType,
  ): Promise<void> {
    return this.delete(`/pages/disable-page/${pageDocumentMappingId}`, {
      params: {
        pageType,
      },
    });
  }
}

export const PagesAPI = new PageClient();
